import React, {
  Attributes,
  Children,
  cloneElement,
  FC,
  isValidElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Head from 'next/head';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import theme from 'src/utils/theme';
import { ProfileDropdown, RestrictToAuth } from 'src/utilityComponents';
import { Header, Page, PageWithSidebar } from 'src/components';
import { EligibilityApi, LanguageApi, MeApi } from 'src/api';
import { getLanguagePair } from 'src/utils/language/getLanguagePair';
import { useGetSavedPath } from 'src/hooks/useGetSavedPath';
import { useScenariosBetaStatus } from 'src/apps/Classic/useScenariosBetaStatus';
import { saveLastLanguagePair } from 'src/utils/language/saveLastLanguagePair';
import { useUserContext } from 'src/context/user';
import { twentyFourHoursAgo } from 'src/apps/DashboardFrame/utils/twentyFourHoursAgo';
import { ROUTES } from 'src/constants/routes.const';
import useSafePush from 'src/hooks/useSafePush';
import { FIVE_MINUTES, ONE_MINUTE } from 'src/constants/times.const';
import { LANGUAGE_SHAPES_BACKGROUND_IMAGES } from 'src/constants/languageShapes';
import { PlatformPage } from 'src/utils/analytics/analytics.const';
import { getAnalyticsLanguageInfo } from 'src/utils/analytics/getAnalyticsLanguageInfo';
import { getAnalyticsUserInfo } from 'src/utils/analytics/getAnalyticsUserInfo';
import { useStreakData } from 'src/hooks/useStreakData';
import { EligibilityResponse } from 'src/api/eligibility/types';
import { UserLanguagesResponse } from 'src/api/me/types';
import { DetailedSourceLanguageResponse } from 'src/api/language/types';
import { useExperienceContext } from 'src/context/experience';
import myJourneyMapImage from 'images/backgrounds/my-journey-map.png';
import { SUPPORTED_AVATAR_TUTOR_LANGUAGE_PAIRS } from 'src/constants/avatarTutor.const';
import { MobileNavBar, Sidebar } from '../ScenarioGeneric/Sidebar';
import DevTools from '../Tools/DevTools';
import Notification from '../Tools/Notification';
import * as S from './styles';
import { getLanguageShapesBackground, getSimpleImageBackground } from './styles';
import { StreakModal } from './StreakModal';

const DashboardFrame: FC<{
  hasSidebar?: boolean;
  sidebarUrl?: string;
  children?: ReactNode;
  setCountry?: (country: string) => void;
  pageScreenName: PlatformPage;
  pageTitle: string;
}> = ({ children, hasSidebar = true, sidebarUrl, setCountry, pageScreenName, pageTitle }) => {
  const intl = useIntl();
  const { safePush } = useSafePush();
  const [languagePairIdFilter, setLanguagePairIdFilter] = useState<number>();
  const { shouldStay } = useScenariosBetaStatus();
  const { user, loading } = useUserContext();
  const { experienceType } = useExperienceContext();
  const [streakModal, setStreakModal] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: theme.sz.ml });

  const {
    data: userLanguagesData,
    isFetching,
    isLoading,
  } = useQuery<UserLanguagesResponse>({
    queryKey: ['userLanguages'],
    queryFn: () => {
      return MeApi.getUserLanguages();
    },
    staleTime: ONE_MINUTE,
    gcTime: ONE_MINUTE,
  });

  useEffect(() => {
    if (loading) return;
    /* istanbul ignore else: empty else branch */
    if (user?.id) {
      window.dataLayer.push({
        event: 'platform',
        page_screen_type: 'Platform',
        page_screen_name: pageScreenName,
        ...getAnalyticsLanguageInfo(intl),
        ...getAnalyticsUserInfo(user),
      });
    }
    // We need to check user.id for the event to only fire once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const saveLastLanguagePairId = useCallback(
    (languagePairId: number, source?: string, target?: string) => {
      setLanguagePairIdFilter(languagePairId);
      saveLastLanguagePair(languagePairId, source, target);
    },
    [],
  );

  useGetSavedPath(isFetching && isLoading ? undefined : userLanguagesData, saveLastLanguagePairId);

  const selectedPath = useMemo(() => {
    return userLanguagesData?.languagePairs.find(
      ({ languagePairId }) => languagePairId === languagePairIdFilter,
    );
  }, [languagePairIdFilter, userLanguagesData?.languagePairs]);

  const { data: sourceLanguages } = useQuery<DetailedSourceLanguageResponse[] | null>({
    queryKey: ['sourceLanguages', selectedPath?.sourceLanguageLocale],
    queryFn: () => {
      /* istanbul ignore if: should be defined */
      if (!selectedPath?.sourceLanguageLocale) return null;
      return LanguageApi.getSourceLanguage(selectedPath?.sourceLanguageLocale);
    },
    enabled: !!selectedPath?.sourceLanguageLocale,
  });

  const sourceLanguage = useMemo(() => {
    return sourceLanguages?.find(({ languagePairId }) => languagePairId === languagePairIdFilter);
  }, [languagePairIdFilter, sourceLanguages]);

  const { data: eligibility } = useQuery<EligibilityResponse | null>({
    queryKey: ['eligibility', selectedPath?.languagePairId],
    queryFn: () => {
      if (!selectedPath?.languagePairId) return null;
      return EligibilityApi.getEligibility(selectedPath?.languagePairId);
    },
    gcTime: FIVE_MINUTES,
    staleTime: FIVE_MINUTES,
  });

  const [contentWidth, setContentWidth] = useState<number | null>(null);
  const resizeObserverRef = useRef<ResizeObserver>(null);

  /* istanbul ignore next: tested visually */
  const onPageContentRendered = useCallback((contentElement: HTMLDivElement | null) => {
    if (!contentElement) {
      resizeObserverRef.current?.disconnect();
      return;
    }

    resizeObserverRef.current = new ResizeObserver(() => {
      setContentWidth(contentElement.offsetWidth);
    });

    resizeObserverRef.current.observe(contentElement);
  }, []);

  const languagePair = getLanguagePair(userLanguagesData, languagePairIdFilter);

  const isSupportedLanguagePairAvatarTutor = selectedPath?.languagePairId
    ? SUPPORTED_AVATAR_TUTOR_LANGUAGE_PAIRS.includes(selectedPath.languagePairId)
    : false;

  const { streakData } = useStreakData({
    enabled: hasSidebar,
    languagePairId: selectedPath?.languagePairId,
  });

  const languageShapeImage = languagePair?.targetLanguageLocale
    ? LANGUAGE_SHAPES_BACKGROUND_IMAGES[languagePair?.targetLanguageLocale]?.default
    : undefined;

  /* istanbul ignore next: tested visually */
  const pageBackground = useMemo(() => {
    if (pageScreenName === PlatformPage.MY_JOURNEY || pageScreenName === PlatformPage.MY_WORDS) {
      if (experienceType === '2024' && pageScreenName === PlatformPage.MY_JOURNEY) {
        return getSimpleImageBackground(myJourneyMapImage);
      } else if (experienceType === '2025' && !isMobile) {
        return getLanguageShapesBackground({
          backgroundImage: languageShapeImage,
        });
      }
    }
  }, [pageScreenName, experienceType, languageShapeImage, isMobile]);

  /* istanbul ignore next: tested visually */
  const pageWithSidebarBackground = useMemo(
    () =>
      pageScreenName === PlatformPage.HOME
        ? getLanguageShapesBackground({
            backgroundImage: languageShapeImage,
            hasSidebar: true,
            hasMiniPlayer: experienceType === '2024',
          })
        : undefined,
    [pageScreenName, experienceType, languageShapeImage],
  );

  const recentlyJoined = user && twentyFourHoursAgo(Date.parse(user.dateJoined));

  // Add properties to child components
  const childrenWithProps = Children.map(children, child => {
    if (isValidElement(child)) {
      return cloneElement(child, {
        studiedLanguagePairs: userLanguagesData?.languagePairs,
        languagePairId: selectedPath?.languagePairId,
        targetLanguageName: selectedPath?.targetLanguageName,
        targetLanguageLocale: selectedPath?.targetLanguageLocale,
        targetLanguageFlagImage: selectedPath?.targetLanguageFlagImage,
        sourceLanguageName: sourceLanguage?.sourceLanguageName,
        sourceLanguageLocale: selectedPath?.sourceLanguageLocale,
        isSupportedLanguagePairCommunicate: eligibility?.eligibleForCommunicate,
        isSupportedLanguagePairImmerse: eligibility?.eligibleForImmerse,
        isSupportedLanguagePairBuddies: eligibility?.eligibleForAiBuddies,
        recentlyJoined,
      } as Attributes);
    }
    return child;
  });

  if (!shouldStay) return null;

  return (
    <RestrictToAuth>
      <Head>
        <title>{pageTitle} - Memrise</title>
      </Head>
      <Notification />
      <DevTools languagePairId={selectedPath?.languagePairId} setCountry={setCountry} />
      <StreakModal
        isOpen={streakModal}
        streak={{
          accomplishedToday: !!streakData?.currentStreak?.accomplishedToday,
          current: streakData?.currentStreak?.count,
          longest: streakData?.longestStreak?.count,
        }}
        onClose={() => setStreakModal(false)}
      />
      {hasSidebar ? (
        <PageWithSidebar
          footerStyle="none"
          header={
            <S.HeaderWrapper>
              <Header
                logoUrl={ROUTES.dashboard}
                dropdown={
                  <ProfileDropdown
                    isSupportedLanguagePairBuddies={eligibility?.eligibleForAiBuddies}
                  />
                }
                lightHeader
                minimalHeader
                hasBetaTag={sidebarUrl === ROUTES.membot.select}
                streakCounter={streakData?.currentStreak?.count}
                streakAccomplishedToday={streakData?.currentStreak?.accomplishedToday}
                onStreakClick={() => setStreakModal(true)}
                isAuthorisedUser={!!user}
              >
                <S.LanguageFilterWrapper>
                  {!isLoading ? (
                    languagePair && (
                      <S.FlagImageWrapper
                        onClick={async () => await safePush(ROUTES.languages)}
                        aria-label={languagePair.targetLanguageName}
                        data-testid="languageImage"
                      >
                        <S.FlagImage src={languagePair.targetLanguageFlagImage} alt="" />
                      </S.FlagImageWrapper>
                    )
                  ) : (
                    <S.Loader />
                  )}
                </S.LanguageFilterWrapper>
              </Header>
            </S.HeaderWrapper>
          }
          sidebar={
            <Sidebar
              url={sidebarUrl}
              showContent={!!languagePair?.targetLanguageName}
              isSupportedLanguagePairBuddies={eligibility?.eligibleForAiBuddies}
              isSupportedLanguagePairAvatarTutor={isSupportedLanguagePairAvatarTutor}
            />
          }
          mobileNavBar={
            languagePair?.targetLanguageName ? (
              <MobileNavBar
                url={sidebarUrl}
                isSupportedLanguagePairBuddies={eligibility?.eligibleForAiBuddies}
                isSupportedLanguagePairAvatarTutor={isSupportedLanguagePairAvatarTutor}
              />
            ) : null
          }
          contentBackground={pageWithSidebarBackground}
        >
          <div ref={onPageContentRendered}>{childrenWithProps}</div>
          <S.PageWithSidebarFloatingPortal $width={contentWidth}>
            <div id="floatingBottomPortalRoot" />
          </S.PageWithSidebarFloatingPortal>
        </PageWithSidebar>
      ) : (
        <Page footerStyle="none" contentBackground={pageBackground}>
          {childrenWithProps}
          <S.FullWidthFloatingPortal>
            <div id="floatingBottomPortalRoot" />
          </S.FullWidthFloatingPortal>
        </Page>
      )}
    </RestrictToAuth>
  );
};

export default DashboardFrame;
